import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as InvestmentIcon,
  //ShowChart as ForecastIcon,
  AccountBalance as BudgetIcon,
  BarChart as MetricsIcon,
  Security as RiskIcon
} from '@mui/icons-material';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableStepContent from '../common/EditableStepContent.tsx';

interface FinancialPlanStepperProps {
  productName?: string;
  initialStep?: number;
  onComplete?: () => void;
  onSubmitRFP?: () => void;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

const MetricChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  color: '#00CC99',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(0, 204, 153, 0.2)',
  }
}));

const FinancialPlanStepper: React.FC<FinancialPlanStepperProps> = ({
  productName = "Product",
  initialStep = 0,
  onComplete,
  onSubmitRFP,
}) => {
  // Add state for costs
  const [stepCosts, setStepCosts] = useState({
    0: 3500, // Financial Assessment
    1: 2800, // Budget & Forecasting
    2: 2200, // Investment Strategy
    3: 1800, // Risk Management
    4: 1500, // Financial Metrics & KPIs
  });

  // Function to update a step's cost
  const handleCostChange = (stepIndex: number, newCost: number) => {
    setStepCosts(prev => ({
      ...prev,
      [stepIndex]: newCost
    }));
  };

  // Define the financial plan steps
  const financialSteps: StepData[] = [
    {
      label: 'Financial Assessment',
      description: 'Evaluate current financial position and investment requirements',
      icon: <AssessmentIcon />,
      cost: stepCosts[0],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Financial Assessment for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Current Financial Position"
            cost={stepCosts[0]}
            onCostChange={(newCost) => handleCostChange(0, newCost)}
            content={
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <MoneyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Cash Flow Analysis" 
                    secondary="Evaluate current cash reserves, operating cash flow, and liquidity position" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <AssessmentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Balance Sheet Review" 
                    secondary="Analyze assets, liabilities, and equity structure" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
              </List>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Investment Requirements
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Capital Expenditure
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Estimate required investments in infrastructure, technology, and equipment.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    Operational Expenditure
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Project ongoing costs for personnel, maintenance, and operational expenses.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Budget & Forecasting',
      description: 'Develop detailed budget projections and financial forecasts',
      icon: <BudgetIcon />,
      cost: stepCosts[1],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Budget & Forecasting for {productName}
          </GradientText>
          
          <EditableStepContent
            title="12-Month Budget Projection"
            cost={stepCosts[1]}
            onCostChange={(newCost) => handleCostChange(1, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Develop quarterly budget allocations for development, marketing, operations, and support.
                - Include contingency reserves and flexibility for scaling based on market response.
              </Typography>
            }
          />
          
          <Divider sx={{ my: 2, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Revenue Forecasting
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Conservative Scenario
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Projected revenue based on minimal market adoption and longer sales cycles.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    Base Scenario
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Projected revenue based on expected market adoption and typical sales cycles.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 111, 0, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#FF6F00', mb: 1 }}>
                    Optimistic Scenario
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Projected revenue based on rapid market adoption and accelerated sales cycles.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Investment Strategy',
      description: 'Outline capital allocation and funding strategies',
      icon: <InvestmentIcon />,
      cost: stepCosts[2],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Investment Strategy for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Capital Allocation"
            cost={stepCosts[2]}
            onCostChange={(newCost) => handleCostChange(2, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Prioritize investments based on strategic importance, ROI potential, and risk profile.
                - Establish stage-gate funding approach with clear milestones for additional investment.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Funding Options
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <MoneyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Internal Funding" 
                  secondary="Allocation from operating budget, reallocation from other initiatives" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <MoneyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="External Funding" 
                  secondary="Debt financing, equity investment, strategic partnerships" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Risk Management',
      description: 'Identify financial risks and develop mitigation strategies',
      icon: <RiskIcon />,
      cost: stepCosts[3],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Risk Management for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Risk Assessment Matrix"
            cost={stepCosts[3]}
            onCostChange={(newCost) => handleCostChange(3, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Identify and categorize financial risks by likelihood and potential impact.
                - Evaluate market risks, operational risks, and compliance risks.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Mitigation Strategies
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Cost Overrun Protection
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Implement stage-gate funding, contingency reserves, and regular budget reviews.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 111, 0, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#FF6F00', mb: 1 }}>
                    Revenue Shortfall Protection
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Develop flexible pricing models, phased rollout strategies, and diversified revenue streams.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Financial Metrics & KPIs',
      description: 'Define key financial metrics and performance indicators',
      icon: <MetricsIcon />,
      cost: stepCosts[4],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Financial Metrics & KPIs for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Key Performance Indicators"
            cost={stepCosts[4]}
            onCostChange={(newCost) => handleCostChange(4, newCost)}
            content={
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
                <MetricChip label="ROI" />
                <MetricChip label="Payback Period" />
                <MetricChip label="Gross Margin" />
                <MetricChip label="Customer Acquisition Cost" />
                <MetricChip label="Customer Lifetime Value" />
                <MetricChip label="Monthly Recurring Revenue" />
                <MetricChip label="Burn Rate" />
                <MetricChip label="Cash Conversion Cycle" />
              </Box>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Monitoring & Reporting
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              - Implement real-time financial dashboards for tracking key metrics.
              - Establish regular reporting cadence with stakeholders.
              - Define thresholds for corrective action and optimization.
            </Typography>
          </ContentBox>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <HolographicStepper 
        steps={financialSteps}
        initialStep={initialStep}
        onComplete={onComplete}
        orientation="horizontal"
        alternativeLabel={true}
      />
    </Box>
  );
};

export default FinancialPlanStepper; 